import {
  FlexCodeTransactionStatus,
  HighnoteTransactionEvent,
  HighnoteTransactionStatus,
  WorkflowStatus,
} from 'API';
import { SAGE300_IMPORT_RETRY_WARNING_PREFIX, UNCONFIRMED_POSTING_PREFIX } from 'util/erpPostingConstants';

const ChargebackStatus: HighnoteTransactionStatus[] = [
  HighnoteTransactionStatus.REFUNDED,
  HighnoteTransactionStatus.REVERSED,
];

/**
 * Converts an amount in integer cents to a float value suitable for Agave posting.
 *
 * - The input `amount` is expected to be in cents (e.g., 12345 for $123.45).
 * - If a transaction `status` is provided and indicates a chargeback
 *   (REFUNDED or REVERSED), the amount will be negated if it is positive.
 * - Returns the amount as a float (e.g., 123.45).
 *
 * @param {number} amount - The amount in integer cents.
 * @param {HighnoteTransactionStatus | null} [status=null] - The transaction status, used to determine if the amount should be negated for chargebacks.
 * @returns {number} The formatted amount as a float.
 */
export const formatAmountForAgavePost = (
  amount: number,
  status: HighnoteTransactionStatus | null = null
): { amountValue: number; amount: string } => {
  // Treat incoming amount as integer cents (may be negative).

  // convert to float from cents
  let amountValue = amount / 100;

  if (status) {
    if (ChargebackStatus.includes(status) && Math.sign(amountValue) === 1) {
      amountValue = -amountValue;
    }
  }

  // return both the string and the float value at once since different ways posting was implemented use different types
  return {
    amountValue,
    amount: amountValue.toFixed(2),
  };
};

// SEE-1390 — whether the paidol's Sage Intacct billable posting is enabled AND
// applicable (dimension-only paidols use the item slot for their cost type, so
// billable can't ride the line). Gates the cost-type -> item auto-derivation.
// Typed loosely because the Paidol model's setting fields are Amplify-generated
// and vary by codegen state; callers pass the selectedPaidol object.
export const isBillablePostingEnabled = (
  paidol?: {
    sageIntacctBillableSettings?: { isEnabled?: boolean | null } | null;
    intacctProjectsNotLicensed?: boolean | null;
  } | null
): boolean => Boolean(paidol?.sageIntacctBillableSettings?.isEnabled) && !paidol?.intacctProjectsNotLicensed;

export const isAgaveTransactionAllowedToCode = (
  transaction?: HighnoteTransactionEvent | null,
  sourceSystemSlug?: string | null
): boolean => {
  // If no transaction, not allowed to code
  if (!transaction) return false;

  // If transaction is bypassed, not allowed to code (acts like posted)
  if (transaction.erpBypassStatus) {
    return false;
  }

  // If transaction is posted (has both erpApprovalStatus and agaveExpenseId), not allowed to code
  if (
    transaction.erpApprovalStatus &&
    (transaction.agaveExpenseId || transaction.erpPostingStatus === 'COMPLETED')
  ) {
    return false;
  }

  // Spectrum, Vista, and QuickBooks Desktop all post asynchronously via a poll
  // (AppXchange 15-30 min; QBD via the Web Connector). Lock coding while the txn
  // is in-flight (PENDING/PROCESSING) so the user can't change codes between the
  // Post click and the poll callback — the charge is already enqueued with the
  // coding snapshot. (SEE-1042)
  if (
    (sourceSystemSlug === 'spectrum' ||
      sourceSystemSlug === 'vista-hosted' ||
      sourceSystemSlug === 'quick-books-desktop') &&
    transaction.erpApprovalStatus &&
    (transaction.erpPostingStatus === 'PENDING' || transaction.erpPostingStatus === 'PROCESSING')
  ) {
    return false;
  }

  // Allow coding for ALL transaction statuses (including REFUNDED, REVERSED, etc.)
  // as long as they are not posted or bypassed
  return true;
};

/**
 * A transaction is "finalized" once posted, bypassed, Flex-approved or Flex-exported.
 * Independent of erpApprovalStatus/returnedStatus; never true for a pending
 * authorization.
 *
 * An exported row still carries flexCodeTransactionStatus APPROVED, so the last two
 * clauses overlap today. The explicit one keeps the lock once SEE-1308 retires the
 * legacy field. Mirrors is_finalized in the CDK's transaction_status.py.
 */
export const isTransactionFinalized = (transaction?: HighnoteTransactionEvent | null): boolean => {
  if (!transaction) return false;
  return (
    !!transaction.erpBypassStatus ||
    !!transaction.agaveExpenseId ||
    transaction.erpPostingStatus === 'PENDING' ||
    transaction.erpPostingStatus === 'PROCESSING' ||
    transaction.erpPostingStatus === 'COMPLETED' ||
    transaction.flexCodeTransactionStatus === FlexCodeTransactionStatus.APPROVED ||
    // Optional chaining + the null guard match workflowStatus.ts: test files declare
    // their own `jest.mock('API')` and a missing enum would otherwise make every
    // transaction with no workflowStatus compare equal to undefined.
    (transaction.workflowStatus != null && transaction.workflowStatus === WorkflowStatus?.EXPORTED)
  );
};

/**
 * Strip the paidol-scoped prefix from an Agave ID.
 * Scoped IDs have the format "{paidolId}#{agaveId}". ERPs need the raw agaveId.
 * Returns the original ID unchanged if it has no '#' separator.
 */
export const stripPaidolPrefix = (scopedId: string | null | undefined): string | undefined => {
  if (!scopedId || !scopedId.includes('#')) return scopedId ?? undefined;
  return scopedId.split('#').slice(1).join('#');
};

export const shouldShowNeedsReviewStatus = (transaction?: HighnoteTransactionEvent | null): boolean => {
  // If no transaction, don't show needs review
  if (!transaction) return false;

  // If transaction is bypassed, don't show needs review (acts like posted)
  if (transaction.erpBypassStatus) {
    return false;
  }

  // If transaction is already approved, don't show needs review
  if (transaction.erpApprovalStatus) {
    return false;
  }

  // Show "Needs Review" for CLEARED, APPROVED, and REFUNDED transactions
  return (
    transaction.status === HighnoteTransactionStatus.CLEARED ||
    transaction.status === HighnoteTransactionStatus.APPROVED ||
    transaction.status === HighnoteTransactionStatus.REFUNDED
  );
};

/**
 * Marker the posting processor writes into `erpPostingError` when it could not
 * tell whether the ERP write landed (SEE-1259). A plain prefix rather than a new
 * ERPPostingStatus value, so no Amplify schema change is needed.
 *
 * Defined in util/erpPostingConstants (SEE-1763) so the direct-QBO posting
 * module can share it without importing from app/services/agave; re-exported
 * here for existing callers.
 */
export { UNCONFIRMED_POSTING_PREFIX };

/**
 * True when the last posting attempt's outcome is unknown — the ERP may already
 * hold it, so the transaction must not be re-posted without the user checking.
 */
export function isUnconfirmedPostingError(error?: string | null): boolean {
  return !!error && error.trimStart().startsWith(UNCONFIRMED_POSTING_PREFIX);
}

/**
 * SEE-1861: true when a still-pending Sage 300 posting carries hh2's import
 * error (the agent is retrying an import Sage refused). Only meaningful while
 * the posting is PENDING/PROCESSING — the backend clears it on the final state.
 */
export function isSageImportRetryWarning(warning?: string | null): boolean {
  return !!warning && warning.startsWith(SAGE300_IMPORT_RETRY_WARNING_PREFIX);
}
